import { DependencyList, useCallback, useMemo } from 'react'
import {
  ApiGetWithToken,
  FetchState,
  useApiFetch,
  useApiFetchList
} from 'shared/api'
import {
  searchApiBy,
  searchsSisetuApiBy
} from 'interface/endpoints'
import {
  ResearchProjectResult,
  ResearchResult,
  ResearchResultList
} from './types'
import { safeParseDate } from 'shared/util'
import { useUpdateTime } from 'interface/util'

export * from './types'

const margeResearchByProject = (researchs: ResearchResult[]): ResearchProjectResult[] => {
  const projectMap: { [projectKey: string]: ResearchProjectResult } = {}
  for(const research of researchs) {
    const projectKey = research.kadaiNo ?? `_${research.bunkenNo}`
    const startNendo = (research.startNendo && research.startNendo > 0) ? research.startNendo : research.nendo
    const endNendo = (research.endNendo && research.endNendo > 0) ? research.endNendo : research.nendo
    const researchers = [
      research.primaryResearcher?.kojinCode ?? research.primaryResearcher?.kojinName,
      ...research.supportResearchers.map(r => r.kojinCode ?? r.kojinName)
    ].flatMap(kojinCode => kojinCode ?? [])

    const project = projectMap[projectKey]
    if (!project) {
      projectMap[projectKey] = {
        projectKey,
        projectName: research.projectName,
        startNendo, endNendo,
        totalCost: research.researchCost ?? 0,
        researchs: [research],
        researchers
      }
    } else {
      if (project.startNendo > startNendo) {
        project.startNendo = startNendo
      }
      if (project.endNendo < endNendo) {
        project.projectName = research.projectName
        project.endNendo = endNendo
      }
      project.totalCost += research.researchCost ?? 0
      project.researchs.push(research)
      researchers.forEach(kojinCode => {
        if (!project.researchers.includes(kojinCode)) {
          project.researchers.push(kojinCode)
        }
      })
    }
  }
  const projectList = Object.values(projectMap)
  projectList.sort((a, b) => b.startNendo - a.startNendo)
  return projectList
}

export const useListResearch = (
  searchId: number | undefined | null,
  sisetuCode: string | undefined | null,
  option: {
    updateTime?: string
    partialLimit?: number
    onPreFetch?: () => void
    onFetched?: (result: ResearchProjectResult[]) => void
  } = {},
  deps?: DependencyList
) => {
  const { updateTime, partialLimit, onPreFetch, onFetched } = option
  const cacheTime = useMemo(() => safeParseDate(updateTime)?.getTime(), [updateTime])
  return useApiFetchList<ResearchResult, ResearchProjectResult[]>(
    useCallback(
      (apiAccess, cancelToken) => {
        if (!searchId) return 0
        if (onPreFetch) onPreFetch()
        return ApiGetWithToken<number>(
          apiAccess,
          sisetuCode ? searchsSisetuApiBy(searchId, sisetuCode, '/research/count', { cacheTime }) :
          searchApiBy(searchId, '/research/count', { cacheTime }),
          cancelToken
        )
      },
      [searchId, cacheTime, sisetuCode, onPreFetch]
    ),
    useCallback(
      (apiAccess, cancelToken, offset, limit) => {
        if (!searchId) return null
        const wrapper = async () => {
          const list = await ApiGetWithToken<ResearchResultList>(
            apiAccess,
            sisetuCode ? searchsSisetuApiBy(searchId, sisetuCode, '/research/list', { cacheTime, offset, limit }) :
            searchApiBy(searchId, '/research/list', { cacheTime, offset, limit }),
            cancelToken
          )
          return list?.list
        }
        return wrapper()
      },
      [searchId, cacheTime, sisetuCode]
    ),
    useCallback(
      (result) => {
        const marged = margeResearchByProject(result)
        if (onFetched) onFetched(marged)
        return marged
      },
      [onFetched]
    ),
    partialLimit ?? 1000,
    deps
  )
}

const useGetResearch = (
  searchId: number | null | undefined,
  bunkenNo: string | null | undefined,
  option: {
    defaultResearch?: ResearchResult
    updateTime?: string
  } = {}
): FetchState<ResearchResult> => {
  const { defaultResearch, updateTime } = option
  const t = useUpdateTime(updateTime)
  return useApiFetch(
    useCallback(
      (apiAccess, cancelToken) => {
        if (!searchId) return null
        if (defaultResearch && defaultResearch.bunkenNo === bunkenNo) return defaultResearch
        return ApiGetWithToken<ResearchResult>(
          apiAccess,
          searchApiBy(searchId, `/research/bunken/${bunkenNo}`, { t }),
          cancelToken
        )
      },
      [searchId, t, bunkenNo, defaultResearch]
    )
  )
}

const useListResearchByKadaiNo = (
  searchId: number | null | undefined,
  kadaiNo: string | null | undefined,
  option: {
    updateTime?: string
  } = {}
): FetchState<ResearchResultList> => {
  const { updateTime } = option
  const t = useUpdateTime(updateTime)
  return useApiFetch(
    useCallback(
      (apiAccess, cancelToken) => {
        if (!searchId) return null
        return ApiGetWithToken<ResearchResultList>(
          apiAccess,
          searchApiBy(searchId, `/research/kadai/${kadaiNo}`, { t }),
          cancelToken
        )
      },
      [searchId, t, kadaiNo]
    )
  )
}

export const useGetResearchProject = (
  searchId: number | null | undefined,
  projectKey: string | null | undefined,
  option: {
    defaultProject?: ResearchProjectResult
    updateTime?: string
  } = {}
): FetchState<ResearchProjectResult> => {
  const { defaultProject, updateTime } = option

  const { bunkenNo, kadaiNo, project } = useMemo(
    () => {
      if (projectKey && projectKey.startsWith('_')) {
        const bunkenNo = projectKey.substring(1)
        if (defaultProject && defaultProject.researchs.some(r => r.bunkenNo === bunkenNo)) {
          return { bunkenNo: null, kadaiNo: null, project: defaultProject }
        } else {
          return { bunkenNo, kadaiNo: null, project: null }
        }
      } else {
        if (defaultProject && defaultProject.projectKey === projectKey) {
          return { bunkenNo: null, kadaiNo: null, project: defaultProject }
        } else {
          const kadaiNo = projectKey ?? null
          return { bunkenNo: null, kadaiNo, project: null }
        }
      }
    },
    [projectKey, defaultProject]
  )
  const researchState = useGetResearch(searchId, bunkenNo, { updateTime })
  const kadaiState = useListResearchByKadaiNo(searchId, kadaiNo, { updateTime })
  return useMemo<FetchState<ResearchProjectResult>>(
    () => {
      if (project) {
        return {
          loading: false,
          error: null,
          data: project,
          refetch: () => {
            console.warn('its static data.')
          }
        }
      } else if (bunkenNo) {
        const projectList = researchState.data ? margeResearchByProject([researchState.data]) : []
        return {
          ...kadaiState,
          data: projectList.length === 1 ? projectList[0] : null
        }
      } else if (kadaiNo) {
        const projectList = kadaiState.data ? margeResearchByProject(kadaiState.data.list) : []
        return {
          ...kadaiState,
          data: projectList.length === 1 ? projectList[0] : null
        }
      }
      return {
        loading: false,
        error: null,
        data: null,
        refetch: () => {
          console.warn('its static data.')
        }
      }
    },
    [bunkenNo, kadaiNo, researchState, kadaiState, project]
  )
}
